<template>
  <panel-layout
    id="role-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="role" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 dark:text-gray-100 leading-none">
            {{ role.isNewRecord ? $t('pages.role.edit.new') : $t('pages.role.edit.label', { name: role.name }) }}
          </h2>
          <div v-if="!role.isNewRecord" class="mt-1">
            <p class="text-gray-700 dark:text-gray-200 text-sm md:text-base" v-html="$t('pages.role.edit.id', { id: role.idCustomRole })" />
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="role" ref="form" class="h-full w-full">
      <o-scroll class="h-full">
        <div class="w-full px-6 py-4">
          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.name')" :info="$t('pages.role.edit.title')">
              <text-field v-model="role.name" :label="$t('general.name')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" class="w-full" :label="$t('general.description')" :info="$t('pages.role.edit.descriptionInfo')">
              <text-area-field v-model="role.information" :label="$t('general.description')" :status="status" />
            </o-field-group>
          </div>

          <div class="w-full mb-3">
            <label class="block text-sm text-gray-700 dark:text-gray-200 font-medium">
              <span>{{ $t('pages.role.edit.permissions') }}</span>
            </label>
          </div>

          <div v-if="bundles" class="flex flex-col">
            <div v-for="bundle in bundles" :key="bundle.name">
              <bundle :bundle="bundle" />
            </div>
          </div>
        </div>
      </o-scroll>
    </o-form>

    <template #footer>
      <div v-if="role">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

// @ts-ignore
import Bundle from '@/components/roles/elements/Bundle.vue'

import {
  TextField,
  TextAreaField,
} from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const role = ref<any>(null)
const bundles = ref<any>(null)
const loading = ref(true)

const confirmDialog = ref<any>()
const form = ref<any>()

const { unsavedChanges: changedRole, snapshot: snapshotRole } = useUnsavedChanges(role)
const { unsavedChanges: changedBundles, snapshot: snapshotBundles } = useUnsavedChanges(bundles)

const unsavedChanges = computed(() => {
  return changedRole.value || changedBundles.value
})

const save = async (close = false) => {
  if (!await form.value?.validate()) return false

  loading.value = true

  try {
    const _role = role.value
    const _bundles = bundles.value

    _role.grantBundles = []

    for (const bundle of _bundles) {
      if (bundle.value) {
        let url = `bundle://${bundle.code}`

        if (bundle.options) {
          const params = new URLSearchParams()
          for (const key in bundle.options) {
            params.append(key, bundle.options[key]?.value)
          }

          url += `?${params.toString()}`
        }
        _role.grantBundles.push(url)
      }
    }

    const response = (await $api.role.save(role.value)).data

    if (response) {
      role.value = response
    }

    const bundleList = await $api.role.bundles()
    setupBundles(bundleList?.items)

    if (response) {
      snapshotRole()
      snapshotBundles()

      $notification.toast({
        title: $i18n.t('pages.role.edit.success'),
        text: $i18n.t('pages.role.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Role', response)

      if (close) {
        panel.close(true)
      }
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }

  loading.value = false

  return false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const setupBundles = (_bundles: any) => {
  const _role = role.value
  const mapped: Record<string, any> = {}
  const staging: any[] = []

  for (const bundle of _role.grantBundles) {
    const url = new URL(bundle)
    const id = (url.host || url.pathname).replace('//', '')
    const params = url.searchParams.entries()

    mapped[id] = {}

    for (const [key, val] of params) {
      mapped[id][key] = val
    }
  }

  for (const bundle of Object.values(_bundles) as any[]) {
    if (bundle) {
      const { name, information, code, options } = bundle
      const value = mapped[code]

      if (options) {
        for (const [key, option] of Object.entries(options) as any[]) {
          option.value = value?.[key] || option.defaultValue
        }
      }

      const data = {
        value: value !== undefined,
        options,
        name,
        information,
        code,
      }

      staging.push(data)
    }
  }

  bundles.value = staging
}

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      role.value = (await $api.role.defaults({} as any)).data
    } else {
      role.value = (await $api.role.get(id)).data
    }

    snapshotRole()

    const _bundles = await $api.role.bundles()

    setupBundles(_bundles?.items)
    snapshotBundles()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })
</script>
